import * as React from 'react';
import { printSltReport } from './printReport';
import { AppUser } from 'Src/views/App/App';
import { insightsRequest, insightsError } from 'Src/helpers/service/sltInsights';
import { SimpleModal } from 'Src/components/SimpleModal/SimpleModal';
import { Card } from './OverviewContent';
import { fullSportResult } from './SltSportResults';
import './SltSport.scss';
const num = v => (v == null ? '—' : Number(v.toFixed(1)).toLocaleString('en-GB'));
const pct = v => (v == null ? '—' : Math.round(v) + '%');
const ordinal = n =>
  n == null ? '—' : n + (n % 100 >= 11 && n % 100 <= 13 ? 'th' : { 1: 'st', 2: 'nd', 3: 'rd' }[n % 10] || 'th');
const labels = { FIXTURE: 'Fixtures', TOURNAMENT: 'Tournaments', INDIVIDUAL: 'Individual', INTERNAL: 'Internal' };
function Podium({ place }: { place: number }) {
  return place >= 1 && place <= 3 ? <span className={'sls-podium place' + place}>{ordinal(place)}</span> : null;
}
function Placement({ value }: { value: any }) {
  return value?.place ? (
    <>
      <b>
        {ordinal(value.place)}
        {value.total ? ' of ' + value.total : ''}
      </b>{' '}
      <Podium place={value.place} />
    </>
  ) : null;
}
function Match({ value }: { value: any }) {
  const label = value?.label || '',
    outcome = value?.outcome;
  const parts = label.split(' · ');
  const ordinary = ['Won', 'Lost', 'Draw', 'Tie'].includes(parts[0]);
  return (
    <>
      <b>{ordinary ? parts.slice(1).join(' · ') : label}</b>
      {outcome && (
        <span className={'slo-delta ' + (outcome === 'WON' ? 'up' : outcome === 'LOST' ? 'dn' : 'neutral')}>
          {outcome === 'DRAW'
            ? 'Drawn'
            : outcome === 'WON'
            ? 'Won'
            : outcome === 'LOST'
            ? 'Lost'
            : outcome === 'TIE'
            ? 'Tied'
            : 'No result'}
        </span>
      )}
    </>
  );
}
function EventResult({ row, canStudents }: { row: any; canStudents: boolean }) {
  if (!row.available) {
    return (
      <span className={row.due ? 'sli-muted' : 'slc-registerWarning'}>{row.due ? 'Result due' : 'Result missing'}</span>
    );
  }
  if (row.kind === 'INTERNAL') {
    return (
      <>
        Winner: <b>{row.winner}</b>
      </>
    );
  }
  if (row.kind === 'INDIVIDUAL') {
    const best = row.individual?.find(person => person.place);
    return (
      <>
        {row.teamPlace && (
          <>
            Team <Placement value={row.teamPlace} /> ·{' '}
          </>
        )}
        {best ? (
          <>
            Best: <b>{ordinal(best.place)}</b>
            {canStudents && best.firstName ? ' (' + best.firstName.slice(0, 1) + '. ' + best.lastName + ', ' : ' · '}
            {[best.year, best.gender === 'FEMALE' ? 'girls' : best.gender === 'MALE' ? 'boys' : null]
              .filter(Boolean)
              .join(' ')}
            {canStudents && best.firstName ? ')' : ''} <small>{fullSportResult(best.result)}</small>
          </>
        ) : (
          <span>Published performance</span>
        )}
      </>
    );
  }
  if (row.kind === 'TOURNAMENT') {
    const value = row.tournament?.best || row.tournament?.paths?.find(path => path.stage);
    return value ? (
      <>
        {value.stage || value.label}{' '}
        {value.place && (
          <>
            {' '}
            · <Placement value={value} />
          </>
        )}
        {!value.place && value.groups?.length ? (
          <small>
            {value.groups
              .map(g =>
                [
                  g.name,
                  g.place ? ordinal(g.place) + ' of ' + g.total : null,
                  g.points != null ? num(g.points) + ' points' : null
                ]
                  .filter(Boolean)
                  .join(' · ')
              )
              .join('; ')}
          </small>
        ) : null}
      </>
    ) : row.result.best?.place ? (
      <Placement value={row.result.best} />
    ) : (
      <span>Tournament result recorded</span>
    );
  }
  return <Match value={row.result.best} />;
}
function StageTrack({ team, large = false }: { team: any; large?: boolean }) {
  return (
    <div className={'sls-stageRow ' + (large ? 'large' : '')}>
      <b>{team.name}</b>
      <div className="sls-stages">
        {team.path.map(stage => (
          <div
            key={stage.id}
            className={(stage.reached ? 'reached ' : '') + (stage.out ? 'out ' : '') + (stage.winner ? 'winner' : '')}
          >
            <i>{stage.winner ? '✓' : ''}</i>
            <span>{stage.name}</span>
          </div>
        ))}
      </div>
      {!team.path.length && <span className="sli-muted">Stages not configured</span>}
    </div>
  );
}
export function SportContent({
  user,
  query,
  data,
  comparison,
  periodName,
  canStudents,
  onStudents,
  onStudent,
  exportSignal,
  onReportStateChange
}: {
  user: AppUser;
  query: any;
  data: any;
  comparison: any;
  periodName: string;
  canStudents: boolean;
  onStudents: (query: any) => void;
  onStudent: (id: string) => void;
  exportSignal: number;
  onReportStateChange?: (state: any) => void;
}) {
  const [state, setState] = React.useState<any>(null),
    [previous, setPrevious] = React.useState<any>(null),
    [retry, setRetry] = React.useState(0),
    [segment, setSegment] = React.useState('ALL'),
    [sport, setSport] = React.useState<any>(null),
    [expanded, setExpanded] = React.useState(false),
    [missingOpen, setMissingOpen] = React.useState(false),
    [drawer, setDrawer] = React.useState<any>(null);
  React.useEffect(() => {
    onReportStateChange?.({ resultType: segment, ...(sport?.id ? { sportId: sport.id } : {}) });
  }, [segment, sport, onReportStateChange]);
  const key = JSON.stringify(query),
    resultsRef = React.useRef<HTMLElement>(null);
  React.useEffect(() => {
    let active = true;
    setState(null);
    setSport(null);
    setSegment('ALL');
    setDrawer(null);
    insightsRequest(user, 'results', query)
      .then(value => {
        if (active) {
          setState({ data: value.sportReport });
        }
      })
      .catch(error => {
        if (active) {
          setState({ error: insightsError(error) });
        }
      });
    return () => {
      active = false;
    };
  }, [key, retry, user.roleSessionKey]);
  React.useEffect(() => {
    let active = true;
    setPrevious(null);
    if (comparison?.comparison?.snapshotId) {
      const q = { ...query, snapshotId: comparison.comparison.snapshotId };
      delete q.from;
      delete q.to;
      insightsRequest(user, 'results', q)
        .then(value => {
          if (active) {
            setPrevious(value.sportReport);
          }
        })
        .catch(() => {});
    }
    return () => {
      active = false;
    };
  }, [key, comparison?.comparison?.snapshotId]);
  React.useEffect(() => {
    if (exportSignal) {
      setDrawer({ kind: 'export' });
    }
  }, [exportSignal]);
  React.useEffect(() => {
    if (!drawer) {
      return;
    }
    const old = document.activeElement as HTMLElement;
    const panel = document.querySelector('.sli-sportDrawer') as HTMLElement;
    if (!panel) {
      return;
    }
    panel.setAttribute('role', 'dialog');
    panel.setAttribute('aria-modal', 'true');
    panel.setAttribute(
      'aria-label',
      drawer.kind === 'tournament' ? 'Tournament details' : drawer.kind === 'event' ? 'Event result' : 'Sport details'
    );
    const focus = () =>
      Array.from(panel.querySelectorAll<HTMLElement>('button:not(:disabled),a[href],input:not(:disabled)')).filter(
        el => !!el.offsetParent
      );
    focus()[0]?.focus();
    const close = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setDrawer(null);
        e.preventDefault();
      }
      if (e.key === 'Tab') {
        const list = focus();
        if (e.shiftKey && document.activeElement === list[0]) {
          list[list.length - 1]?.focus();
          e.preventDefault();
        } else if (!e.shiftKey && document.activeElement === list[list.length - 1]) {
          list[0]?.focus();
          e.preventDefault();
        }
      }
    };
    document.addEventListener('keydown', close);
    return () => {
      document.removeEventListener('keydown', close);
      if (old?.isConnected) {
        old.focus();
      }
    };
  }, [!!drawer, drawer?.kind]);
  const report = state?.data;
  if (!report) {
    return (
      <div className="sls-content">
        <p role="status">{state?.error || 'Loading sport evidence…'}</p>
        {state?.error && (
          <button className="sli-button" onClick={() => setRetry(retry + 1)}>
            Retry
          </button>
        )}
      </div>
    );
  }
  const go = (q: any) => {
    if (canStudents) {
      setDrawer(null);
      onStudents({ area: 'sport', ...q });
    } else {
      setDrawer({ kind: 'access' });
    }
  };
  const jump = () =>
    requestAnimationFrame(() => resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }));
  const resultRows = report.results.filter(row => !sport || row.sportId === sport.id),
    filtered = resultRows.filter(row => segment === 'ALL' || row.kind === segment),
    entered = filtered.filter(row => row.available),
    missing = filtered.filter(row => !row.available),
    visible = expanded ? entered : entered.slice(0, 10);
  const wdl = { won: 0, drawn: 0, lost: 0, tied: 0, noResult: 0 };
  for (const row of filtered.filter(row => row.kind === 'FIXTURE' && row.available)) {
    const k = { WON: 'won', DRAW: 'drawn', LOST: 'lost', TIE: 'tied', NO_RESULT: 'noResult' }[row.result.best?.outcome];
    if (k) {
      wdl[k]++;
    }
  }
  const decisions = [
    {
      name: 'Represented the school',
      value: report.measured ? report.represented : null,
      sub: report.measured
        ? pct(report.representedRate) + ' of roster in a fixture, tournament or event'
        : 'Needs participation records',
      delta:
        previous?.measured && report.measured && report.represented != null && previous.represented != null
          ? report.represented - previous.represented
          : null,
      click: () => go({ segment: 'represented' })
    },
    {
      name: 'Fixtures played',
      value: report.fixturesPlayed,
      sub:
        [
          report.split.fixtures ? report.split.fixtures + ' fixtures' : null,
          report.split.tournaments ? report.split.tournaments + ' tournaments' : null,
          report.split.individual ? report.split.individual + ' individual' : null,
          report.split.unclassified ? report.split.unclassified + ' unclassified' : null
        ]
          .filter(Boolean)
          .join(' · ') || 'No classified external events ended',
      delta: previous ? report.fixturesPlayed - previous.fixturesPlayed : null,
      click: jump
    },
    ...[
      ['Girls in school sport', report.gender, 'gender:FEMALE', 'boys', report.thresholds.gender],
      ['PP in school sport', report.pp, 'pupilsPremium:yes', 'non-PP', report.thresholds.equity]
    ].map(([name, g, char, peer, threshold]: any) => ({
      name,
      value: report.measured ? g.value : null,
      unit: 'percent',
      sub: !g.known
        ? char === 'gender:FEMALE'
          ? 'Gender not recorded in MIS'
          : 'PP not recorded in MIS'
        : g.value == null
        ? 'Fewer than 10 students — hidden'
        : report.measured
        ? 'vs ' + pct(g.peerValue) + ' of ' + peer
        : 'Needs participation records',
      pill:
        report.measured && g.gap != null && g.gap <= -threshold
          ? char === 'gender:FEMALE'
            ? Math.round(-g.gap) + ' pts behind boys'
            : '−' + Math.round(-g.gap) + ' pts'
          : null,
      click: () => go({ segment: 'notrepresented', characteristic: char })
    }))
  ];
  const date = (value, range = false) => {
    const year = new Date(report.asOf).getUTCFullYear(),
      from = new Date(Date.UTC(year, new Date(report.asOf).getUTCMonth() < 8 ? -4 : 8, 1));
    return new Date(value).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      ...(!range && +new Date(value) < +from ? { year: 'numeric' as const } : {}),
      timeZone: report.timezone
    });
  };
  const summary =
    segment === 'ALL' || segment === 'FIXTURE' ? (
      filtered.some(row => row.kind === 'FIXTURE' && row.available) ? (
        <>
          <div className="sls-wdl">
            {['won', 'drawn', 'lost'].map(key => (
              <span key={key}>
                <b>{wdl[key]}</b>
                <small>{key}</small>
              </span>
            ))}
          </div>
          <b>
            {wdl.won + wdl.drawn + wdl.lost + wdl.tied
              ? Math.round((100 * wdl.won) / (wdl.won + wdl.drawn + wdl.lost + wdl.tied)) + '% won'
              : 'Win rate unavailable'}
            {wdl.tied ? ' · ' + wdl.tied + ' tied' : ''}
            {wdl.noResult ? ' · ' + wdl.noResult + ' no result' : ''}
          </b>
        </>
      ) : (
        <span className="sli-muted">No fixture results entered yet</span>
      )
    ) : segment === 'TOURNAMENT' ? (
      <span>
        {new Set(filtered.map(row => row.tournamentId || row.id)).size} entered ·{' '}
        {filtered.filter(row => (row.tournament?.best || row.result?.best)?.place <= 3).length} podiums ·{' '}
        {filtered.filter(row => (row.tournament?.best || row.result?.best)?.place === 1).length} winner
      </span>
    ) : segment === 'INDIVIDUAL' ? (
      <span>
        {filtered.length} {filtered.length === 1 ? 'event' : 'events'} ·{' '}
        {sport ? '—' : num(report.summary.individualStudents)} students competed ·{' '}
        {filtered.reduce((sum, row) => sum + row.individual.filter(p => p.place && p.place <= 3).length, 0)} top-3
        finishes
      </span>
    ) : (
      <span>
        {filtered.length} house events
        {report.summary.leader
          ? ' · Leading: ' + report.summary.leader[0] + ' (' + report.summary.leader[1] + ' wins)'
          : ''}
      </span>
    );
  const renderRow = (row: any) => (
    <tr
      key={row.id}
      role="button"
      tabIndex={0}
      aria-label={'Open result ' + row.name}
      onClick={() => setDrawer({ kind: 'event', value: row })}
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          setDrawer({ kind: 'event', value: row });
        }
      }}
    >
      <td>{date(row.at)}</td>
      <td>
        <b>{row.name}</b>
        <small>{[row.teams.join(', '), row.homeAway?.toLowerCase(), row.venue].filter(Boolean).join(' · ')}</small>
      </td>
      <td>
        <span className="slo-delta neutral">
          {row.kind === 'FIXTURE'
            ? 'Fixture'
            : row.kind === 'TOURNAMENT'
            ? 'Tournament'
            : row.kind === 'INDIVIDUAL'
            ? 'Individual'
            : 'Internal'}
        </span>
      </td>
      <td>
        <EventResult row={row} canStudents={canStudents} />
      </td>
      <td>›</td>
    </tr>
  );
  const list = (people: any[]) => (
    <div className="sls-finishers">
      {people.slice(0, 10).map((person, index) => (
        <button
          key={person.id || index}
          disabled={!canStudents || !person.id}
          onClick={() => {
            setDrawer(null);
            onStudent(person.id);
          }}
        >
          <span>
            {ordinal(person.place)} <Podium place={person.place} />
          </span>
          <b>
            {canStudents && person.firstName
              ? [person.firstName, person.lastName].join(' ')
              : [person.year, person.gender === 'FEMALE' ? 'girls' : person.gender === 'MALE' ? 'boys' : null]
                  .filter(Boolean)
                  .join(' ')}
          </b>
          <span>{person.category || person.year}</span>
          <small>{fullSportResult(person.result)}</small>
        </button>
      ))}
    </div>
  );
  const tournamentInfo = (t: any) => (
    <>
      <p>
        {[
          t.sport,
          t.genderLabel,
          t.ages.length ? t.ages.join(', ') : null,
          date(t.at, true) + '–' + date(t.end || t.at, true),
          t.venue,
          t.totalTeams + (t.totalTeams === 1 ? ' team' : ' teams'),
          t.format
        ]
          .filter(Boolean)
          .join(' · ')}
      </p>
      {t.paths.map(team => (
        <section key={team.id}>
          <StageTrack team={team} large />
          <h3>{team.name}</h3>
          {team.games.length ? (
            <table className="slc-table">
              <thead>
                <tr>
                  <th>Opponent</th>
                  <th>Stage</th>
                  <th>Result</th>
                </tr>
              </thead>
              <tbody>
                {team.games.map(game => (
                  <tr key={game.id}>
                    <td>{game.opponent || 'Opponent pending'}</td>
                    <td>{game.stage || '—'}</td>
                    <td>
                      {game.available ? (
                        <Match value={game.result} />
                      ) : (
                        <span className="sli-muted">{game.ended ? 'Result missing' : 'Upcoming'}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="sli-muted">No games scheduled yet.</p>
          )}
          <p>
            {team.place ? (
              <Placement value={team} />
            ) : (
              team.label ||
              team.groups
                .map(g =>
                  [
                    g.name,
                    g.place ? ordinal(g.place) + ' of ' + g.total : null,
                    g.points != null ? num(g.points) + ' points' : null
                  ]
                    .filter(Boolean)
                    .join(' · ')
                )
                .join('; ') ||
              'Final placing not recorded'
            )}
          </p>
        </section>
      ))}
      {t.individual && (
        <>
          <h3>Official school points</h3>
          {t.officialScores?.length ? (
            t.officialScores.map((score, index) => (
              <p key={index}>
                {score.team} · {num(score.points)} points{score.group ? ' · ' + score.group : ''}
              </p>
            ))
          ) : (
            <p className="sli-muted">Official school points not recorded.</p>
          )}
          <h3>Our finishers</h3>
          {list(t.finishers)}
        </>
      )}
      <p>{num(t.students)} students</p>
      <button
        className="sli-button"
        disabled={!canStudents || t.students == null}
        onClick={() => go({ tournamentId: t.id, segment: 'all' })}
      >
        See students
      </button>
    </>
  );
  function csvCertificate(t: any) {
    if (!canStudents) {
      return;
    }
    const content =
      'Position,Student,Category,Result\n' +
      (t.certificateEntries?.length ? t.certificateEntries : t.entrants || [])
        .map(p =>
          [
            p.place,
            [p.firstName, p.lastName].join(' '),
            p.category || p.year,
            p.result
              ? fullSportResult(p.result)
              : (p.teams || [])
                  .map(name => [name, t.paths.find(team => team.name === name)?.label].filter(Boolean).join(' · '))
                  .join('; ')
          ]
            .map(
              v =>
                '"' +
                String(v ?? '')
                  .replace(/"/g, '""')
                  .replace(/^[=+@-]/, "'$&") +
                '"'
            )
            .join(',')
        )
        .join('\n');
    const url = URL.createObjectURL(new Blob([content], { type: 'text/csv;charset=utf-8' })),
      a = document.createElement('a');
    a.href = url;
    a.download = 'tournament-certificates.csv';
    a.click();
    URL.revokeObjectURL(url);
  }
  return (
    <div className="sls-content">
      <div className="slo-kpis four">
        {decisions.map((k: any) => (
          <button
            key={k.name}
            className={'slo-kpi sli-kpi ' + (k.value == null ? 'unavailable' : '')}
            onClick={k.click}
          >
            <span className="slo-kpiLabel">
              <span>{k.name}</span>
              {k.value != null &&
                (k.pill ? (
                  <span className="slo-delta dn">{k.pill}</span>
                ) : k.delta != null ? (
                  <span className={'slo-delta ' + (k.delta >= 0 ? 'up' : 'dn')}>
                    {k.delta > 0 ? '+' : ''}
                    {num(k.delta)}
                  </span>
                ) : null)}
            </span>
            <span className="slo-kpiValue">
              <strong>{k.unit === 'percent' ? pct(k.value) : num(k.value)}</strong>
            </span>
            <small>{k.sub}</small>
            <span className="slo-chevron">›</span>
          </button>
        ))}
      </div>
      <div className="slo-two">
        <Card
          title="Girls and boys by year"
          subtitle={'Share who represented the school · alert at ' + report.thresholds.gender + ' pts'}
          action={
            <div className="slo-legend">
              <span>
                <i className="girls" />
                Girls
              </span>
              <span>
                <i className="boys" />
                Boys
              </span>
            </div>
          }
        >
          {report.years.map(year => (
            <div className="slo-genderRow" key={year.key}>
              <b>{year.label}</b>
              {['girls', 'boys'].map(side => (
                <button
                  className={side}
                  key={side}
                  onClick={() =>
                    go({
                      years: year.key,
                      characteristic: side === 'girls' ? 'gender:FEMALE' : 'gender:MALE',
                      segment: 'represented'
                    })
                  }
                  disabled={year[side] == null}
                >
                  {year[side] == null ? (
                    <em className="sls-hatched">&lt;10</em>
                  ) : (
                    <>
                      <i style={{ width: Math.min(82, year[side] * 0.82) + '%' }} />
                      <span
                        style={
                          side === 'girls'
                            ? { right: Math.min(82, year[side] * 0.82) + '%' }
                            : { left: Math.min(82, year[side] * 0.82) + '%' }
                        }
                      >
                        {pct(year[side])}
                      </span>
                    </>
                  )}
                </button>
              ))}
            </div>
          ))}
          {!report.years.length && <p className="sli-muted">No measured school sport for these year groups.</p>}
        </Card>
        <Card title="Squad depth" subtitle="Players per sport this term">
          {report.sports.map(item => (
            <button
              key={item.id}
              className="sls-squad"
              onClick={() => go({ sportId: item.id, segment: 'represented' })}
            >
              <b>{item.name}</b>
              <div className="slo-track">
                <i
                  style={{
                    width: (100 * (item.players || 0)) / Math.max(1, ...report.sports.map(s => s.players || 0)) + '%'
                  }}
                />
              </div>
              <span>
                <b>{num(item.players)}</b> players ·{' '}
                {item.teams.length ? item.teams.length + (item.teams.length === 1 ? ' team' : ' teams') : 'squad'} ·{' '}
                {pct(item.girls)} girls
              </span>
            </button>
          ))}
          {!report.sports.length && <p className="sli-muted">No classified sport events in this period.</p>}
        </Card>
      </div>
      <Card title="Sports" subtitle={periodName + ' so far'} body={false}>
        <div className="sls-tableWrap">
          <table className="slc-table">
            <thead>
              <tr>
                {['Sport', 'Teams', 'Players', 'Girls', 'PP', 'Events', 'Results'].map(text => (
                  <th key={text}>{text}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {report.sports.map(item => (
                <tr
                  key={item.id}
                  tabIndex={0}
                  role="button"
                  onClick={() => {
                    setSport(item);
                    setSegment('ALL');
                    jump();
                  }}
                  onKeyDown={e => {
                    if (e.key === 'Enter') {
                      setSport(item);
                      setSegment('ALL');
                      jump();
                    }
                  }}
                >
                  <td>
                    <b>{item.name}</b>
                  </td>
                  <td>{item.teams.join(', ') || 'Squad'}</td>
                  <td>{num(item.players)}</td>
                  <td>{pct(item.girls)}</td>
                  <td>{pct(item.pp)}</td>
                  <td>{item.events}</td>
                  <td>
                    {item.fixtureResults ? (
                      <>
                        {item.wdl.won}–{item.wdl.drawn}–{item.wdl.lost}
                        {item.best ? ' · best ' + ordinal(item.best.place) : ''}
                        {item.wdl.tied ? ' · ' + item.wdl.tied + ' tied' : ''}
                        {item.wdl.noResult ? ' · ' + item.wdl.noResult + ' no result' : ''}
                      </>
                    ) : item.best ? (
                      <>
                        Best: <Placement value={item.best} />
                      </>
                    ) : item.publishedResults ? (
                      <span className="sli-muted">Result recorded · no placing</span>
                    ) : (
                      <span className="slc-registerWarning">No results yet</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
      <section ref={resultsRef} id="slt-sport-results">
        <Card
          title="Results"
          subtitle="Ended events in the selected period"
          body={false}
          action={
            <div className="sls-resultControls">
              {sport && (
                <button className="sli-chip" onClick={() => setSport(null)}>
                  {sport.name} ×
                </button>
              )}
              <div className="sli-density">
                {['ALL', 'FIXTURE', 'TOURNAMENT', 'INDIVIDUAL', 'INTERNAL']
                  .filter(type => type === 'ALL' || resultRows.some(row => row.kind === type))
                  .map(type => (
                    <button
                      key={type}
                      className={segment === type ? 'on' : ''}
                      onClick={() => {
                        setSegment(type);
                        setExpanded(false);
                        setMissingOpen(false);
                      }}
                    >
                      {type === 'ALL' ? 'All' : labels[type]}{' '}
                      {type === 'ALL' ? resultRows.length : resultRows.filter(row => row.kind === type).length}
                    </button>
                  ))}
              </div>
            </div>
          }
        >
          <div className="sls-summary">{summary}</div>
          <div className="sls-tableWrap">
            <table className="slc-table">
              <thead>
                <tr>
                  {['Date', 'Event', 'Type', 'Result', ''].map((text, i) => (
                    <th key={i}>{text}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {visible.map(renderRow)}
                {missing.length > 0 && (
                  <>
                    <tr>
                      <td colSpan={5}>
                        <div className="sls-missing">
                          <button
                            className="sli-link"
                            aria-expanded={missingOpen}
                            onClick={() => setMissingOpen(!missingOpen)}
                          >
                            <b>
                              {missing.length} {missing.length === 1 ? 'event is' : 'events are'} missing a result
                            </b>{' '}
                            {missingOpen ? '⌃' : '⌄'}
                          </button>
                          <button className="sli-button" onClick={() => setDrawer({ kind: 'message' })}>
                            Nudge coaches
                          </button>
                        </div>
                      </td>
                    </tr>
                    {missingOpen && missing.map(renderRow)}
                  </>
                )}
                {!filtered.length && (
                  <tr>
                    <td colSpan={5}>No ended events in this selection.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {!expanded && entered.length > 10 && (
            <button className="sli-link sls-showAll" onClick={() => setExpanded(true)}>
              Show all {entered.length}
            </button>
          )}
          {report.split.unclassified > 0 && (
            <p className="sls-unclassified">
              {report.split.unclassified} {report.split.unclassified === 1 ? 'event has' : 'events have'} no type ·{' '}
              <button className="sli-link" onClick={() => setDrawer({ kind: 'classify' })}>
                Classify →
              </button>
            </p>
          )}
        </Card>
      </section>
      <Card title="Tournaments" subtitle="Tournaments our teams entered this period" body={false}>
        {report.tournaments.length ? (
          <div className="sls-tournaments">
            {report.tournaments.map(t => (
              <button key={t.id} className="sls-tournament" onClick={() => setDrawer({ kind: 'tournament', value: t })}>
                <div className="sls-tournamentTitle">
                  <b>{t.name}</b>
                  {t.status !== 'finished' && (
                    <span className={'slo-delta ' + (t.status === 'progress' ? 'blue' : 'neutral')}>
                      {t.status === 'progress' ? 'In progress' : 'Upcoming'}
                    </span>
                  )}
                </div>
                <p>
                  {[
                    t.sport,
                    t.genderLabel,
                    t.totalTeams + (t.totalTeams === 1 ? ' team' : ' teams'),
                    date(t.at, true) + (t.end ? '–' + date(t.end, true) : '')
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </p>
                {t.paths.map(team => (
                  <StageTrack key={team.id} team={team} />
                ))}
                <p>
                  {t.status === 'upcoming' ? (
                    'Starts ' +
                    date(t.at, true) +
                    ' · ' +
                    t.paths.length +
                    (t.paths.length === 1 ? ' team entered' : ' teams entered')
                  ) : t.best ? (
                    <>
                      <Placement value={t.best} /> · {t.best.stage}
                    </>
                  ) : t.status === 'progress' ? (
                    'Through to ' + (t.paths.find(p => p.stage)?.stage || 'group stage')
                  ) : (
                    t.paths
                      .map(
                        p =>
                          p.label ||
                          p.groups
                            .map(g => g.name + (g.place ? ' · ' + ordinal(g.place) + ' of ' + g.total : ''))
                            .join('; ')
                      )
                      .filter(Boolean)
                      .join(' · ') || 'Final placing not recorded'
                  )}
                </p>
                <footer>
                  {num(t.students)} students <span>›</span>
                </footer>
              </button>
            ))}
          </div>
        ) : (
          <p className="sls-emptyTournaments">No tournaments entered this period.</p>
        )}
      </Card>
      <SimpleModal
        isOpen={!!drawer}
        side="right"
        title={
          drawer?.kind === 'tournament'
            ? 'Tournament'
            : drawer?.kind === 'event'
            ? 'Result'
            : drawer?.kind === 'export'
            ? 'PE & Sport Premium report'
            : 'SLT Insights'
        }
        customClass="sli-drawer sli-clubDrawer sli-sportDrawer"
        showFooter={false}
        onCloseClick={() => setDrawer(null)}
        dialogStyle={{ width: 560, maxWidth: '100vw' }}
      >
        {drawer && (
          <div className="sls-drawer">
            <div className={'sls-drawerBody ' + (drawer.kind === 'export' ? 'sls-print' : '')}>
              {drawer.kind === 'event' ? (
                <>
                  <h2>{drawer.value.name}</h2>
                  <p>
                    {[date(drawer.value.at), drawer.value.venue, drawer.value.teams.join(', ')]
                      .filter(Boolean)
                      .join(' · ')}
                  </p>
                  <span className="slo-delta neutral">
                    {drawer.value.kind === 'FIXTURE' ? 'Fixture' : labels[drawer.value.kind]}
                  </span>
                  <div className="sls-evidence">
                    <EventResult row={drawer.value} canStudents={canStudents} />
                  </div>
                  {drawer.value.kind === 'FIXTURE' && drawer.value.scorersRecorded && (
                    <>
                      <h3>Scorers</h3>
                      {drawer.value.scorers.map((p, i) => (
                        <p key={i}>
                          {p.name}
                          {p.minute != null ? ' · ' + p.minute + '′' : ''}
                          {p.ownGoal ? ' · Own goal' : ''}
                        </p>
                      ))}
                    </>
                  )}
                  {drawer.value.tournamentId && (
                    <button
                      className="sli-button"
                      onClick={() => {
                        const t = report.tournaments.find(t => t.id === drawer.value.tournamentId);
                        if (t) {
                          setDrawer({ kind: 'tournament', value: t });
                        }
                      }}
                      disabled={!report.tournaments.some(t => t.id === drawer.value.tournamentId)}
                    >
                      Open tournament
                    </button>
                  )}
                  {drawer.value.kind === 'INDIVIDUAL' && (
                    <>
                      <h3>Our finishers</h3>
                      {list(drawer.value.individual)}
                    </>
                  )}
                  <p>{num(drawer.value.attended)} players attended</p>
                  <button
                    className="sli-button"
                    disabled={!canStudents || drawer.value.attended == null}
                    onClick={() =>
                      go({
                        ...(drawer.value.tournamentId
                          ? { tournamentId: drawer.value.tournamentId }
                          : { eventId: drawer.value.id }),
                        segment: 'attended'
                      })
                    }
                  >
                    See students
                  </button>
                </>
              ) : drawer.kind === 'tournament' ? (
                <>
                  <h2>{drawer.value.name}</h2>
                  {tournamentInfo(drawer.value)}
                </>
              ) : drawer.kind === 'message' ? (
                <>
                  <h2>Connect General messages</h2>
                  <p>
                    To send reminders or share results with parents, connect the General messages module for your
                    school.
                  </p>
                  <p className="sli-muted">No messages have been sent.</p>
                </>
              ) : drawer.kind === 'classify' ? (
                <>
                  <h2>Event classification</h2>
                  <p>
                    These events do not have enough structured information to identify a competition type. Open the
                    event in the school calendar and review its type, sport and participants. Training and Friendly
                    labels will be added separately.
                  </p>
                  {report.unclassified.map(event => (
                    <p key={event.id}>{event.name}</p>
                  ))}
                </>
              ) : drawer.kind === 'access' ? (
                <>
                  <h2>Student access required</h2>
                  <p>
                    Your SLT access includes aggregate figures. Ask your school administrator for student detail access.
                  </p>
                </>
              ) : (
                <>
                  <h2>PE & Sport Premium report</h2>
                  <p>
                    {periodName} · {date(report.period.from, true)}–
                    {date(new Date(+new Date(report.period.to) - 1).toISOString(), true)}
                  </p>
                  <p>
                    Represented the school: {report.measured ? num(report.represented) : '—'} · Fixtures played:{' '}
                    {report.fixturesPlayed}
                  </p>
                  <p>
                    Observed participation and published results. This pack does not include premium spending or claim
                    statutory compliance.
                  </p>
                  <button className="sli-button sli-primary" onClick={event => printSltReport(event.currentTarget)}>
                    Print / save as PDF
                  </button>
                </>
              )}
            </div>
            {drawer.kind === 'tournament' && (
              <footer className="slc-drawerFooter">
                <button className="sli-button sli-primary" onClick={() => setDrawer({ kind: 'message' })}>
                  Share result with parents
                </button>
                <button
                  className="sli-button"
                  disabled={!canStudents || (!drawer.value.finishers.length && !drawer.value.entrants?.length)}
                  onClick={() => csvCertificate(drawer.value)}
                >
                  Export certificate list
                </button>
              </footer>
            )}
          </div>
        )}
      </SimpleModal>
    </div>
  );
}
